import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Building,
  Calendar,
  FileText,
  DollarSign,
  User,
  MapPin,
  Phone,
  Mail,
  Package,
  ArrowLeft
} from 'lucide-react';
import { accountsPayableService } from '@/services/accountsPayableService';
import type { AccountPayable } from '@/types/accounts-payable.types';

// En lugar de usar mock data local, vamos a usar el hook useAccountsPayable
import { useAccountsPayable } from '@/hooks/useAccountsPayable';

export const AccountPayableDetailPage: React.FC = () => {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const { accounts, loading } = useAccountsPayable();
  const [account, setAccount] = useState<AccountPayable | null>(null);

  useEffect(() => {
    console.log('AccountPayableDetailPage - accountId:', accountId);
    console.log('AccountPayableDetailPage - accounts:', accounts);
    
    if (accountId) {
      // Si tenemos cuentas del hook, las usamos
      if (accounts.length > 0) {
        const foundAccount = accounts.find(acc => acc.id === accountId);
        console.log('AccountPayableDetailPage - foundAccount:', foundAccount);
        setAccount(foundAccount || null);
      } else {
        // Fallback: crear una cuenta mock para testing
        const mockAccount: AccountPayable = {
          id: accountId,
          accountNumber: 'CXP-2024-001',
          supplierId: 'SUP-001',
          supplierName: 'Distribuidora Alimentos SAC',
          purchaseOrderNumber: 'OC-2024-0156',
          originalAmount: 350.00,
          paidAmount: 150.00,
          pendingAmount: 200.00,
          status: 'partially_paid',
          dueDate: '2024-02-17',
          createdAt: '2024-01-18',
          description: 'Compra de insumos para producción'
        };
        setAccount(mockAccount);
      }
    }
  }, [accountId, accounts]);

  if (loading || !account) {
    return (
      <div className="p-6 h-full flex items-center justify-center">
        <div className="text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">
            {loading ? 'Cargando cuenta...' : 'Cuenta no encontrada'}
          </h2>
          {!loading && (
            <button 
              onClick={() => navigate('/accounts-payable')}
              className="mt-4 text-blue-600 hover:text-blue-800"
            >
              Volver a Cuentas por Pagar
            </button>
          )}
        </div>
      </div>
    );
  }

  const isOverdue = accountsPayableService.isOverdue(account);

  // Mock data para simular items de la orden de compra
  const purchaseOrderItems = [
    {
      id: '1',
      productName: 'Harina de Trigo Premium',
      quantity: 50,
      unit: 'kg',
      unitPrice: 3.50,
      totalPrice: 175.00
    },
    {
      id: '2',
      productName: 'Aceite Vegetal',
      quantity: 20,
      unit: 'lt',
      unitPrice: 4.25,
      totalPrice: 85.00
    },
    {
      id: '3',
      productName: 'Sal Marina',
      quantity: 10,
      unit: 'kg',
      unitPrice: 2.50,
      totalPrice: 25.00
    }
  ];

  return (
    <div className="p-6 space-y-6 h-full overflow-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          onClick={() => navigate('/accounts-payable')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900">
            Detalle de Cuenta por Pagar
          </h1>
          <p className="text-gray-600 mt-1">
            {account.accountNumber} - {account.supplierName}
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Información General */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building className="w-5 h-5" />
                Información de la Cuenta
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-600">Número de Cuenta</label>
                <p className="text-lg font-semibold">{account.accountNumber}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Orden de Compra</label>
                <p className="text-sm">{account.purchaseOrderNumber}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Estado</label>
                <div className="flex gap-2 mt-1">
                  <Badge className={accountsPayableService.getStatusColor(account.status)}>
                    {accountsPayableService.getStatusText(account.status)}
                  </Badge>
                  {isOverdue && (
                    <Badge variant="destructive">Vencida</Badge>
                  )}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Fecha de Creación</label>
                <p className="text-sm flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {accountsPayableService.formatDate(account.createdAt)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Fecha de Vencimiento</label>
                <p className={`text-sm flex items-center gap-1 ${isOverdue ? 'text-red-600' : ''}`}>
                  <Calendar className="w-4 h-4" />
                  {account.dueDate ? accountsPayableService.formatDate(account.dueDate) : 'No definida'}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Información Financiera
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-600">Monto Recibido</label>
                <p className="text-lg font-semibold text-gray-900">
                  {accountsPayableService.formatCurrency(account.receivedAmount || account.originalAmount)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Monto Pagado</label>
                <p className="text-lg font-semibold text-green-600">
                  {accountsPayableService.formatCurrency(account.paidAmount)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Saldo Pendiente</label>
                <p className={`text-lg font-semibold ${
                  account.pendingAmount === 0 
                    ? 'text-green-600' 
                    : isOverdue 
                      ? 'text-red-600' 
                      : 'text-yellow-600'
                }`}>
                  {accountsPayableService.formatCurrency(account.pendingAmount)}
                </p>
              </div>
              {account.description && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Descripción</label>
                  <p className="text-sm text-gray-700">{account.description}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Información del Proveedor */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5" />
              Información del Proveedor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-600">Proveedor</label>
                  <p className="text-lg font-semibold">{account.supplierName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">ID del Proveedor</label>
                  <p className="text-sm text-gray-600">{account.supplierId}</p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-600">Contacto</label>
                  <p className="text-sm flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    +51 987 654 321
                  </p>
                  <p className="text-sm flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    contacto@proveedor.com
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Detalle de la Orden de Compra */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="w-5 h-5" />
              Detalle de la Orden de Compra
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Producto</TableHead>
                    <TableHead className="text-center">Cantidad Recibida</TableHead>
                    <TableHead className="text-center">Unidad</TableHead>
                    <TableHead className="text-right">Precio Unitario</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {purchaseOrderItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.productName}</TableCell>
                      <TableCell className="text-center">{item.quantity}</TableCell>
                      <TableCell className="text-center">{item.unit}</TableCell>
                      <TableCell className="text-right">
                        {accountsPayableService.formatCurrency(item.unitPrice)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {accountsPayableService.formatCurrency(item.totalPrice)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableRow className="border-t-2 border-gray-300 bg-gray-50">
                  <TableCell colSpan={4} className="text-right font-bold">
                    Total General:
                  </TableCell>
                  <TableCell className="text-right font-bold text-lg">
                    {accountsPayableService.formatCurrency(account.originalAmount)}
                  </TableCell>
                </TableRow>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Historial de Pagos */}
        {account.paidAmount > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Historial de Pagos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Método</TableHead>
                      <TableHead className="text-right">Monto</TableHead>
                      <TableHead>Referencia</TableHead>
                      <TableHead>Estado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell>{accountsPayableService.formatDate(account.createdAt)}</TableCell>
                      <TableCell>Transferencia Bancaria</TableCell>
                      <TableCell className="text-right text-green-600 font-medium">
                        {accountsPayableService.formatCurrency(account.paidAmount)}
                      </TableCell>
                      <TableCell>TXN-001234</TableCell>
                      <TableCell>
                        <Badge className="bg-green-100 text-green-800">Confirmado</Badge>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center pt-4 border-t">
        <div className="text-sm text-gray-600">
          Última actualización: {accountsPayableService.formatDate(account.createdAt)}
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate('/accounts-payable')}>
            Volver a Cuentas por Pagar
          </Button>
        </div>
      </div>
    </div>
  );
};